import { STORE_ORIGIN } from '../config';
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import './Home.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FiHeart, FiEye, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import HeroSection from '../components/HeroSection';

const PRODUCT_IMAGES_URL = `${STORE_ORIGIN}/admin/uploads/products`;
const CATEGORY_IMAGES_URL = `${STORE_ORIGIN}/admin/uploads/categories`;
const BRAND_IMAGES_URL = `${STORE_ORIGIN}/admin/uploads/brands`;

// Fixed Marquee Component
const MarqueeBrands = ({ brands }) => {
  if (!brands || brands.length === 0) return null;

  // Create 3 copies for seamless scrolling
  const marqueeItems = [...brands, ...brands, ...brands];

  return (
    <div className="marquee-container">
      <div className="marquee-track">
        {marqueeItems.map((b, index) => (
          <div key={`${b.id}-${index}`} className="marquee-item">
            <div className="brand-wrapper">
              <img
                src={b.logo || '/placeholder.png'}
                alt={b.name}
                className="brand-logo"
              />
            </div>
          </div>
        ))}
      </div>
      {/* Gradient overlays for smooth edges */}
      <div className="left-gradient-overlay"></div>
      <div className="right-gradient-overlay"></div>
    </div>
  );
};

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const marqueeSpeed = 'medium';

  const fetchAll = useCallback(async () => {
    const extractFilename = path => path?.split('?')[0].split('/').pop();
    try {
      setLoading(true);

      const [slugRes, catRes, featuredRes, brandRes] = await Promise.all([
        fetch(`${STORE_ORIGIN}/api/test.php`),
        fetch(`${STORE_ORIGIN}/api/hompage_categories.php`),
        fetch(`${STORE_ORIGIN}/api/api.php`),
        fetch(`${STORE_ORIGIN}/api/homepage_api.php`)
      ]);

      const slugData = await slugRes.json();
      const catData = await catRes.json();
      const featuredData = await featuredRes.json();
      const brandData = await brandRes.json();

      // --- Categories ---
      const categoriesMap = {};
      catData.categories.forEach(c => {
        categoriesMap[c.category_id] = {
          id: c.category_id,
          name: c.name,
          image: `${CATEGORY_IMAGES_URL}/${c.image}`,
        };
      });
      slugData.forEach(main => {
        main.categories?.forEach(c => {
          if (categoriesMap[c.id]) {
            categoriesMap[c.id].slug = c.slug;
            categoriesMap[c.id].mainSlug = main.slug;
          }
        });
      });
      setCategories(Object.values(categoriesMap));

      // --- Brands ---
      const formattedBrands = (brandData.brands || []).map(b => ({
        id: b.id || b.brand_id,
        name: b.name,
        logo: b.logo ? `${BRAND_IMAGES_URL}/${extractFilename(b.logo)}` : '/placeholder.png'
      }));
      setBrands(formattedBrands);

      // --- Featured Products ---
      const featuredProductsMap = new Map();
      (featuredData || []).forEach(main =>
        main.categories?.forEach(cat =>
          cat.subcategories?.forEach(sub =>
            sub.products?.forEach(p => {
              if (p.is_featured && !featuredProductsMap.has(p.id)) {
                featuredProductsMap.set(p.id, {
                  id: p.id,
                  name: p.name,
                  price: p.base_price,
                  slug: p.slug || p.name.replace(/\s+/g, '-').toLowerCase(),
                  image: p.main_image ? `${PRODUCT_IMAGES_URL}/${extractFilename(p.main_image)}` : '/placeholder.png'
                });
              }
            })
          )
        )
      );
      setFeaturedProducts(Array.from(featuredProductsMap.values()));

    } catch (e) {
      console.error('Error fetching homepage data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const FeaturedNextArrow = ({ onClick }) => (
    <div onClick={onClick} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 cursor-pointer text-gray-700 hover:text-red-600 text-2xl">
      <FiChevronRight />
    </div>
  );

  const FeaturedPrevArrow = ({ onClick }) => (
    <div onClick={onClick} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 cursor-pointer text-gray-700 hover:text-red-600 text-2xl">
      <FiChevronLeft />
    </div>
  );

  const productSlider = {
    infinite: true,
    slidesToShow: 4,
    arrows: true,
    nextArrow: <FeaturedNextArrow />,
    prevArrow: <FeaturedPrevArrow />,
    autoplay: true,
    autoplaySpeed: 2500,
    speed: 1200,
    cssEase: 'ease-in-out',
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3 } },
      { breakpoint: 768, settings: { slidesToShow: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 1 } }
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-red-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <HeroSection />

      {/* BRANDS MARQUEE */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">Top Brands</h2>
          
          {/* Optional Speed Controls */}
          
          
          <MarqueeBrands brands={brands} speed={marqueeSpeed} />
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">Shop by Category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map(c => (
              <Link key={c.id} to={`/${c.mainSlug}/${c.slug}`}>
                <div className="relative rounded-xl overflow-hidden shadow group hover:shadow-lg transition-shadow">
                  <img src={c.image} alt={c.name} className="h-48 w-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/50 flex items-end p-3 text-white font-semibold">
                    {c.name}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">Featured Products</h2>

          {featuredProducts.length === 0 && (
            <p className="text-center text-gray-500">No featured products available.</p>
          )}

          {featuredProducts.length === 1 ? (
            <div className="max-w-xs mx-auto">
              <div className="bg-white rounded-xl shadow hover:shadow-lg transition">
                <Link to={`/product/${featuredProducts[0].slug}`}>
                  <div className="relative pt-[100%]">
                    <img src={featuredProducts[0].image} alt={featuredProducts[0].name} className="absolute inset-0 w-full h-full object-contain p-4" />
                    <div className="absolute top-3 right-3 flex gap-2">
                      <FiHeart />
                      <FiEye />
                    </div>
                  </div>
                </Link>
                <div className="p-4">
                  <h3 className="font-semibold">{featuredProducts[0].name}</h3>
                  <p className="text-red-600 font-bold mt-1">QAR {featuredProducts[0].price}</p>
                </div>
              </div>
            </div>
          ) : (
            <Slider {...productSlider}>
              {featuredProducts.map(p => (
                <div key={p.id} className="px-2">
                  <div className="bg-white rounded-xl shadow hover:shadow-lg transition-transform hover:scale-105 duration-300">
                    <Link to={`/product/${p.slug}`}>
                      <div className="relative pt-[100%]">
                        <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-contain p-4" />
                        <div className="absolute top-3 right-3 flex gap-2">
                          <FiHeart />
                          <FiEye />
                        </div>
                      </div>
                    </Link>
                    <div className="p-4">
                      <h3 className="font-semibold">{p.name}</h3>
                      <p className="text-red-600 font-bold mt-1">QAR {p.price}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;